/**
 * templateCatalog — the studio template dimension (§3.5): the demo exposes
 * exactly TWO templates, "Utazási biztosítás" (travel, with an OPTIONAL
 * flight) and "Általános biztosítás" (assistance). The demo is TEMPLATES-ONLY:
 * there is no style choice — each template FIXES its canvas style (travel IS
 * the boarding-pass canvas, assistance IS the generic card), so this module is
 * also the single home for that template → style mapping and for the legacy
 * style-link → template redirect. The template lives in the URL
 * (`/studio/apple/:template`) under Hungarian slugs. Kept React-free so it can
 * be unit-tested in the node-env vitest.
 */

import { tHu, type Translate } from '../../i18n/messages';
import type { PassTemplateKind } from '../../types';
import type { AppleStyle, GoogleStyle } from '../../walletStyles';

import type { StyleCardOption } from './StyleGallery';

/** URL slugs — Hungarian, matching the demo's operator-facing copy. */
export type StudioTemplateSlug = 'utazas' | 'altalanos';

/** The two template kinds the studios offer (subset of PassTemplateKind). */
export type StudioTemplateKind = Extract<PassTemplateKind, 'travel' | 'assistance'>;

export const STUDIO_TEMPLATE_KINDS: ReadonlyArray<StudioTemplateKind> = ['travel', 'assistance'];

const SLUG_TO_KIND: Record<StudioTemplateSlug, StudioTemplateKind> = {
  utazas: 'travel',
  altalanos: 'assistance',
};

const KIND_TO_SLUG: Record<StudioTemplateKind, StudioTemplateSlug> = {
  travel: 'utazas',
  assistance: 'altalanos',
};

/**
 * Validates an untrusted URL segment the same way the studios validate
 * `:style` — anything but a known slug yields `null`, which renders the
 * template picker instead of trusting the param.
 */
export function templateKindFromSlug(slug: string | undefined): StudioTemplateKind | null {
  if (slug === 'utazas' || slug === 'altalanos') return SLUG_TO_KIND[slug];
  return null;
}

export function templateSlugFor(kind: StudioTemplateKind): StudioTemplateSlug {
  return KIND_TO_SLUG[kind];
}

/**
 * Fixed per-template canvas styles — the demo offers NO style choice: the
 * template alone decides the canvas. Travel IS the boarding-pass/flight
 * layout (the only one with a flight subscription behind it), assistance IS
 * the generic card.
 */
export const STUDIO_APPLE_STYLE: Record<StudioTemplateKind, AppleStyle> = {
  travel: 'boardingPass',
  assistance: 'generic',
};

export const STUDIO_GOOGLE_STYLE: Record<StudioTemplateKind, GoogleStyle> = {
  travel: 'flight',
  assistance: 'generic',
};

/**
 * Legacy single-segment style deep link (`/studio/<platform>/<style>`, from
 * the pre-templates-only eras) → the template slug that now owns that layout:
 * `generic` was always the assistance default → `altalanos`; every other
 * known style lived under travel → `utazas`. Unknown segments yield `null`
 * (the caller falls back to the template picker). `knownStyles` is the
 * calling platform's own style values (APPLE_STYLES / GOOGLE_STYLES), so an
 * Apple-only style can never redirect inside the Google studio.
 */
export function templateSlugForLegacyStyle(
  styleSegment: string,
  knownStyles: ReadonlyArray<string>,
): StudioTemplateSlug | null {
  if (!knownStyles.includes(styleSegment)) return null;
  return styleSegment === 'generic' ? 'altalanos' : 'utazas';
}

/** Operator-facing title per template — reused by picker cards and gate copy. */
export function studioTemplateTitles(t: Translate): Record<StudioTemplateKind, string> {
  return {
    travel: t('template.travel.title'),
    assistance: t('template.assistance.title'),
  };
}

/**
 * The picker cards (both studios) — the ONLY choice the demo offers. The
 * travel card carries the 'Élő' badge, and its copy is honest about the
 * optional flight: live status exists only when a flight is provided at mint.
 */
export function studioTemplateCards(
  t: Translate,
): ReadonlyArray<StyleCardOption<StudioTemplateSlug>> {
  const titles = studioTemplateTitles(t);
  return [
    {
      value: 'utazas',
      title: titles.travel,
      description: t('template.travel.description'),
      badge: t('template.liveBadge'),
    },
    {
      value: 'altalanos',
      title: titles.assistance,
      description: t('template.assistance.description'),
    },
  ];
}

/**
 * Hungarian baselines — the studios translate via the builders above; these
 * constants remain as the language-independent default (and the node-env
 * catalog tests pin the Hungarian copy through them).
 */
export const STUDIO_TEMPLATE_TITLES: Record<StudioTemplateKind, string> = studioTemplateTitles(tHu);

export const STUDIO_TEMPLATE_CARDS: ReadonlyArray<StyleCardOption<StudioTemplateSlug>> =
  studioTemplateCards(tHu);
